import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getSubscriptionRemainingMs,
  SUBSCRIPTION_LOGOUT_GRACE_SECONDS,
} from '../utils/subscriptionPlan';
import CompanyAPI from '../restAPI/CompanyAPI';

/**
 * Detects when subscription end date+time passes during an active session,
 * marks the company subscriptionExpired flag, and starts auto-logout countdown.
 */
export default function useSubscriptionExpiryWatcher({
  endDate,
  companyId,
  groupId,
  token,
  hasPlan,
  isExempt,
  subscriptionActive,
  onSubscriptionExpired,
  onAutoLogout,
}) {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutSecondsRemaining, setLogoutSecondsRemaining] = useState(null);

  const expiryHandledRef = useRef(false);
  const sessionWasActiveRef = useRef(false);
  const logoutTriggeredRef = useRef(false);

  useEffect(() => {
    expiryHandledRef.current = false;
    sessionWasActiveRef.current = false;
    logoutTriggeredRef.current = false;
    setShowLogoutModal(false);
    setLogoutSecondsRemaining(null);
  }, [companyId, endDate]);

  useEffect(() => {
    if (!endDate || !hasPlan) {
      return;
    }

    const remainingMs = getSubscriptionRemainingMs(endDate);
    if (remainingMs !== null && remainingMs > 0) {
      sessionWasActiveRef.current = true;
    }
  }, [endDate, hasPlan, companyId, subscriptionActive]);

  const handleExpiry = useCallback(async () => {
    if (expiryHandledRef.current) {
      return;
    }

    const remainingMs = getSubscriptionRemainingMs(endDate);
    if (remainingMs === null || remainingMs > 0) {
      return;
    }

    // Already expired when this company was selected — not a mid-session transition.
    if (!sessionWasActiveRef.current) {
      expiryHandledRef.current = true;
      return;
    }

    expiryHandledRef.current = true;

    if (companyId && token && groupId) {
      try {
        await CompanyAPI.markSubscriptionExpired(
          token,
          groupId,
          companyId,
          companyId
        );
      } catch (err) {
        console.error('Failed to mark company subscription expired:', err?.message || err);
      }
    }

    onSubscriptionExpired?.();

    if (!isExempt && sessionWasActiveRef.current) {
      setShowLogoutModal(true);
      setLogoutSecondsRemaining(SUBSCRIPTION_LOGOUT_GRACE_SECONDS);
    }
  }, [companyId, endDate, groupId, token, isExempt, onSubscriptionExpired]);

  useEffect(() => {
    if (!endDate || !hasPlan) {
      return undefined;
    }

    const checkExpiry = () => {
      const remainingMs = getSubscriptionRemainingMs(endDate);
      if (remainingMs !== null && remainingMs <= 0) {
        handleExpiry();
      }
    };

    checkExpiry();
    const intervalId = setInterval(checkExpiry, 1000);
    return () => clearInterval(intervalId);
  }, [endDate, hasPlan, handleExpiry]);

  useEffect(() => {
    if (!showLogoutModal || logoutSecondsRemaining == null) {
      return undefined;
    }

    if (logoutSecondsRemaining <= 0) {
      if (!logoutTriggeredRef.current) {
        logoutTriggeredRef.current = true;
        onAutoLogout?.();
      }
      return undefined;
    }

    const intervalId = setInterval(() => {
      setLogoutSecondsRemaining((current) =>
        current != null && current > 0 ? current - 1 : 0
      );
    }, 1000);

    return () => clearInterval(intervalId);
  }, [showLogoutModal, logoutSecondsRemaining, onAutoLogout]);

  return {
    showLogoutModal,
    logoutSecondsRemaining,
  };
}
